/**
 * Owner-only data-export page (issue #6).
 *
 * Build an AI-training-ready dataset from the endoscopy archive: filter
 * by date / procedure / media / annotation / lesion / landmark, preview
 * the volume, create a job, watch it build, then download the whole
 * archive or shard-by-shard (resumable). Talks to the standalone
 * export-service via the viewer proxy at /viewer-api/export/*.
 */
import { createSignal, createMemo, For, Show, onCleanup } from "solid-js";
import { createStore } from "solid-js/store";
import { auth, can } from "../auth/store";
import { taxaFor, type Procedure } from "../endoscopy/lesionTaxonomy";

interface Filters {
  date_from?: string; date_to?: string;
  procedures?: string[];
  media?: ("image" | "video")[];
  annotated_only?: boolean;
  report_status?: string[];
  lesion_ids?: string[];
  landmark_ids?: string[];
  mrn?: string;
  accession?: string;
}

interface Shard { idx: number; name: string; status: string; sha256: string | null; bytes: number; samples: number }
interface Job {
  id: string; status: string; filters: Filters; options: any;
  total_samples: number; done_samples: number; total_bytes: number; shard_count: number;
  error: string | null; created_at: string;
}

function token(): string { return auth()?.accessToken ?? ""; }
async function api(path: string, init?: RequestInit): Promise<any> {
  const r = await fetch(`/viewer-api/export${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token()}`, ...(init?.body ? { "content-type": "application/json" } : {}), ...(init?.headers ?? {}) },
  });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.headers.get("content-type")?.includes("json") ? r.json() : r.text();
}
function fmtBytes(n: number): string {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export function ExportPage() {
  return (
    <Show when={can("data.export")} fallback={
      <div class="grid h-full place-items-center text-ink-700">Data export is restricted to the owner role.</div>
    }>
      <ExportInner />
    </Show>
  );
}

function ExportInner() {
  const GB = 1024 * 1024 * 1024;
  const [filters, setFilters] = createStore<Filters>({ media: ["image", "video"] });
  const [shardBytes, setShardBytes] = createSignal(16 * GB);
  const [format, setFormat] = createSignal<"media" | "dicom">("media");
  const [preview, setPreview] = createSignal<{ instances: number; studies: number; est_bytes: number } | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [jobs, setJobs] = createSignal<Job[]>([]);
  const [shardsByJob, setShardsByJob] = createStore<Record<string, Shard[]>>({});
  const [err, setErr] = createSignal<string | null>(null);

  // Procedure-scoped taxonomy for the lesion/landmark pickers. The
  // export filter spans both procedures, so offer the union.
  const lesions = createMemo(() => {
    const seen = new Set<string>();
    return (["egd", "colonoscopy"] as Procedure[]).flatMap((p) => taxaFor(p, "lesion"))
      .filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)));
  });
  const landmarks = createMemo(() => {
    const seen = new Set<string>();
    return (["egd", "colonoscopy"] as Procedure[]).flatMap((p) => taxaFor(p, "landmark"))
      .filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)));
  });

  function toggleArr<K extends keyof Filters>(key: K, val: string) {
    const cur = (filters[key] as string[] | undefined) ?? [];
    setFilters(key as any, (cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val]) as any);
  }
  const has = (key: keyof Filters, val: string) => ((filters[key] as string[] | undefined) ?? []).includes(val);

  async function doPreview() {
    setErr(null);
    try { setPreview(await api("/preview", { method: "POST", body: JSON.stringify(filters) })); }
    catch (e: any) { setErr(String(e.message ?? e)); }
  }
  async function refreshJobs() {
    try { const r = await api("/jobs"); setJobs(r.jobs ?? []); }
    catch { /* ignore transient */ }
  }
  async function loadShards(id: string) {
    try { const r = await api(`/jobs/${id}`); setShardsByJob(id, r.shards ?? []); }
    catch { /* ignore */ }
  }
  async function createJob() {
    setBusy(true); setErr(null);
    try {
      await api("/jobs", { method: "POST", body: JSON.stringify({ filters, options: { shard_bytes: shardBytes(), format: format() } }) });
      await refreshJobs();
    } catch (e: any) { setErr(String(e.message ?? e)); }
    finally { setBusy(false); }
  }
  async function cancelJob(id: string) { await api(`/jobs/${id}/cancel`, { method: "POST" }).catch(() => {}); refreshJobs(); }
  async function deleteJob(id: string) { await api(`/jobs/${id}`, { method: "DELETE" }).catch(() => {}); refreshJobs(); }

  // Poll while any job is active.
  refreshJobs();
  const timer = setInterval(() => { if (jobs().some((j) => j.status === "building" || j.status === "queued")) refreshJobs(); }, 3000);
  onCleanup(() => clearInterval(timer));

  const dl = (path: string) => `/viewer-api/export${path}?access_token=${encodeURIComponent(token())}`;
  const chip = (active: boolean) =>
    `rounded border px-2 py-0.5 text-[11px] transition ${active ? "border-brand bg-brand/20 text-white" : "border-ink-300 text-ink-700 hover:text-white"}`;

  return (
    <div class="flex h-full flex-col overflow-auto bg-ink-0 text-ink-900">
      <div class="border-b border-ink-300 px-6 py-3">
        <h1 class="text-lg font-semibold text-white">Data export</h1>
        <p class="text-[11px] text-ink-700">Owner-only · AI-training-ready bundles (images + videos + annotations + clinical metadata), resumable.</p>
      </div>

      <div class="grid grid-cols-1 gap-4 p-6 lg:grid-cols-2">
        {/* ---------- Filter builder ---------- */}
        <div class="rounded border border-ink-300 bg-ink-100 p-4 space-y-3 text-[12px]">
          <div class="text-[10px] uppercase tracking-wider text-ink-700">filter</div>

          <div class="flex gap-2">
            <label class="flex-1">Date from
              <input type="date" class="mt-0.5 w-full rounded border border-ink-300 bg-ink-200 px-2 py-1 text-ink-900"
                     onInput={(e) => setFilters("date_from", e.currentTarget.value || undefined)} /></label>
            <label class="flex-1">Date to
              <input type="date" class="mt-0.5 w-full rounded border border-ink-300 bg-ink-200 px-2 py-1 text-ink-900"
                     onInput={(e) => setFilters("date_to", e.currentTarget.value || undefined)} /></label>
          </div>

          <div>
            <div class="mb-1 text-[10px] uppercase text-ink-700">procedure</div>
            <div class="flex gap-1">
              <For each={["EGD", "Colonoscopy"]}>{(p) => (
                <button class={chip(has("procedures", p))} onClick={() => toggleArr("procedures", p)}>{p}</button>
              )}</For>
            </div>
          </div>

          <div>
            <div class="mb-1 text-[10px] uppercase text-ink-700">media</div>
            <div class="flex gap-1">
              <For each={["image", "video"]}>{(m) => (
                <button class={chip(has("media", m))} onClick={() => toggleArr("media", m)}>{m}</button>
              )}</For>
            </div>
          </div>

          <div>
            <div class="mb-1 text-[10px] uppercase text-ink-700">report status</div>
            <div class="flex gap-1">
              <For each={["draft", "finalized", "amended"]}>{(s) => (
                <button class={chip(has("report_status", s))} onClick={() => toggleArr("report_status", s)}>{s}</button>
              )}</For>
            </div>
          </div>

          <label class="flex items-center gap-2">
            <input type="checkbox" onChange={(e) => setFilters("annotated_only", e.currentTarget.checked || undefined)} />
            annotated images only
          </label>

          <details>
            <summary class="cursor-pointer text-[11px] text-ink-700">lesion / landmark class filters</summary>
            <div class="mt-2 max-h-40 overflow-auto rounded border border-ink-300 bg-ink-200/40 p-2">
              <div class="mb-1 text-[10px] uppercase text-ink-700">lesion</div>
              <div class="flex flex-wrap gap-1">
                <For each={lesions()}>{(t) => (
                  <button class={chip(has("lesion_ids", t.id))} onClick={() => toggleArr("lesion_ids", t.id)}>{t.name}</button>
                )}</For>
              </div>
              <div class="mb-1 mt-2 text-[10px] uppercase text-ink-700">landmark</div>
              <div class="flex flex-wrap gap-1">
                <For each={landmarks()}>{(t) => (
                  <button class={chip(has("landmark_ids", t.id))} onClick={() => toggleArr("landmark_ids", t.id)}>{t.name}</button>
                )}</For>
              </div>
            </div>
          </details>

          <div class="flex gap-2">
            <label class="flex-1">MRN
              <input class="mt-0.5 w-full rounded border border-ink-300 bg-ink-200 px-2 py-1 text-ink-900"
                     onInput={(e) => setFilters("mrn", e.currentTarget.value || undefined)} /></label>
            <label class="flex-1">Accession
              <input class="mt-0.5 w-full rounded border border-ink-300 bg-ink-200 px-2 py-1 text-ink-900"
                     onInput={(e) => setFilters("accession", e.currentTarget.value || undefined)} /></label>
          </div>
        </div>

        {/* ---------- Preview + options + create ---------- */}
        <div class="rounded border border-ink-300 bg-ink-100 p-4 space-y-3 text-[12px]">
          <div class="text-[10px] uppercase tracking-wider text-ink-700">build</div>

          <button class="rounded border border-ink-300 bg-ink-200 px-3 py-1.5 text-[12px] hover:text-white" onClick={doPreview}>Preview volume</button>
          <Show when={preview()}>
            <div class="rounded bg-ink-200/50 px-3 py-2 text-[12px]">
              ≈ <span class="text-white">{preview()!.studies}</span> studies ·
              <span class="text-white"> {preview()!.instances}</span> instances ·
              ~<span class="text-white">{fmtBytes(preview()!.est_bytes)}</span> (source DICOM)
            </div>
          </Show>

          <div>
            <div class="mb-1 text-[10px] uppercase text-ink-700">media format</div>
            <div class="flex gap-1">
              <button class={chip(format() === "media")} onClick={() => setFormat("media")}>JPG / MP4 (extracted)</button>
              <button class={chip(format() === "dicom")} onClick={() => setFormat("dicom")}>DICOM (.dcm)</button>
            </div>
            <p class="mt-1 text-[10px] text-ink-700">
              {format() === "dicom"
                ? "Raw Part-10 DICOM per instance — full headers + pixel data, original transfer syntax."
                : "Extracted JPEG frames + MP4 clips — smaller, drops straight into image/video loaders."}
            </p>
          </div>

          <label class="block">Shard size
            <select class="ml-2 rounded border border-ink-300 bg-ink-200 px-2 py-1 text-ink-900"
                    onChange={(e) => setShardBytes(Number(e.currentTarget.value))}>
              <option value={512 * 1024 * 1024}>512 MB</option>
              <option value={1 * GB}>1 GB</option>
              <option value={4 * GB}>4 GB</option>
              <option value={16 * GB} selected>16 GB (recommended)</option>
              <option value={32 * GB}>32 GB</option>
              <option value={64 * GB}>64 GB</option>
            </select>
            <span class="ml-2 text-[10px] text-ink-700">videos run 200–500 MB each — larger shards keep a procedure's clips together</span>
          </label>

          <button class="w-full rounded border border-brand bg-brand/15 px-3 py-2 font-medium text-brand hover:bg-brand hover:text-white disabled:opacity-50"
                  disabled={busy()} onClick={createJob}>
            {busy() ? "Creating…" : "Create export job"}
          </button>
          <Show when={err()}><div class="rounded bg-danger/15 px-3 py-2 text-[11px] text-danger">{err()}</div></Show>
          <p class="text-[10px] text-ink-700">
            Output: WebDataset tar shards ({format() === "dicom" ? "{key}.dcm + {key}.json" : "{key}.jpg|mp4 + {key}.json"}) + manifest.jsonl + shards.json + SHA256SUMS.
            Downloads are resumable (HTTP Range); verify with the per-shard sha256. For large sets, download per-shard rather than the whole archive.
          </p>
        </div>
      </div>

      {/* ---------- Jobs ---------- */}
      <div class="px-6 pb-8">
        <div class="mb-2 text-[10px] uppercase tracking-wider text-ink-700">jobs</div>
        <Show when={jobs().length} fallback={<p class="text-[12px] text-ink-700">No export jobs yet.</p>}>
          <div class="space-y-2">
            <For each={jobs()}>{(j) => {
              const pct = () => j.total_samples ? Math.round((j.done_samples / j.total_samples) * 100) : 0;
              return (
                <div class="rounded border border-ink-300 bg-ink-100 p-3 text-[12px]">
                  <div class="flex items-center gap-3">
                    <span class="font-mono text-[11px] text-ink-700">{j.id.slice(0, 8)}</span>
                    <span class="rounded px-2 py-0.5 text-[10px] uppercase tracking-wider"
                          classList={{ "bg-brand/20 text-brand": j.status === "building" || j.status === "queued",
                                       "bg-ok/20 text-ok": j.status === "ready",
                                       "bg-danger/20 text-danger": j.status === "failed",
                                       "bg-ink-200 text-ink-700": j.status === "cancelled" || j.status === "expired" }}>
                      {j.status}
                    </span>
                    <span class="text-ink-700">{j.done_samples}/{j.total_samples} samples · {j.shard_count} shards · {fmtBytes(Number(j.total_bytes))}</span>
                    <span class="ml-auto flex gap-2">
                      <Show when={j.status === "building" || j.status === "queued"}>
                        <button class="text-[11px] text-ink-700 hover:text-danger" onClick={() => cancelJob(j.id)}>cancel</button>
                      </Show>
                      <Show when={j.status === "ready"}>
                        {/* `download` (same-origin) streams the file to disk
                            via the browser's own download UI WITHOUT
                            navigating away — a plain href would unmount the
                            SPA and leave a blank page. */}
                        <a class="text-[11px] text-brand hover:underline" href={dl(`/jobs/${j.id}/archive.tar`)}
                           download={`endopod-export-${j.id.slice(0, 8)}.tar`} rel="noopener">download all</a>
                        <a class="text-[11px] text-ink-700 hover:text-white" href={dl(`/jobs/${j.id}/manifest.jsonl`)}
                           download={`manifest-${j.id.slice(0, 8)}.jsonl`} rel="noopener">manifest</a>
                        <button class="text-[11px] text-ink-700 hover:text-white" onClick={() => loadShards(j.id)}>shards ▾</button>
                      </Show>
                      <button class="text-[11px] text-ink-700 hover:text-danger" onClick={() => deleteJob(j.id)}>delete</button>
                    </span>
                  </div>
                  <Show when={j.status === "building" || j.status === "queued"}>
                    <div class="mt-2 h-1.5 w-full overflow-hidden rounded bg-ink-300">
                      <div class="h-full bg-brand transition-all" style={{ width: `${pct()}%` }} />
                    </div>
                  </Show>
                  <Show when={j.error}><div class="mt-1 text-[11px] text-danger">{j.error}</div></Show>
                  <Show when={shardsByJob[j.id]?.length}>
                    <div class="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4">
                      <For each={shardsByJob[j.id]}>{(s) => (
                        <a class="truncate rounded bg-ink-200/50 px-2 py-1 text-[10px] hover:text-white"
                           href={dl(`/jobs/${j.id}/shards/${s.idx}`)} download={s.name} rel="noopener"
                           title={`${s.samples} samples · ${fmtBytes(Number(s.bytes))} · sha256 ${s.sha256?.slice(0, 12)}…`}>
                          {s.name} · {fmtBytes(Number(s.bytes))}
                        </a>
                      )}</For>
                    </div>
                  </Show>
                </div>
              );
            }}</For>
          </div>
        </Show>
      </div>
    </div>
  );
}
